import {
  AspectRatio,
  Box,
  BoxProps,
  Image,
  ImageProps,
  Skeleton,
} from '@chakra-ui/react';
import { NestedMunicipality } from '@habity/types';
import { useInView } from 'react-intersection-observer';
import { ListSkeleton } from '~/components/homes/list/components/ListSkeleton';
import { getCloudflareStaticMapUrlForMunicipality } from '~/lib/providers/cloudflare/images';

const placeholderImage =
  'https://media.istockphoto.com/id/1334792752/photo/swedish-countryside-view-with-farms-and-fields-in-autumn-colours.jpg?s=612x612&w=0&k=20&c=3kU0XBfnN1KJWs2VxTaO0nDNGxZ8Q85LgRdoFl8-Etw=';

interface MunicipalityImageCardProps {
  municipality: NestedMunicipality;
  boxProps?: BoxProps;
  aspectRatio?: number;
  borderTopRadius?: string;
  borderBottomRadius?: string;
  imageProps?: ImageProps;
  isLoading?: boolean;
  spacing?: 'default' | 'compact';
}

export const MunicipalityImageCard = ({
  municipality,
  boxProps,
  aspectRatio = 1 / 1.2,
  borderTopRadius = '4px',
  borderBottomRadius = '4px',
  imageProps,
  isLoading = false,
}: MunicipalityImageCardProps) => {
  const { name, location } = municipality || {};
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0,
  });

  if (isLoading) {
    return (
      <Box
        position="relative"
        borderTopRadius={borderTopRadius}
        borderBottomRadius={borderBottomRadius}
        overflow="hidden"
        backgroundColor="blackAlpha.50"
        {...boxProps}
      >
        <AspectRatio ratio={aspectRatio}>
          <Skeleton height="100%" width="100%" />
        </AspectRatio>
      </Box>
    );
  }
  return (
    <Box
      ref={ref}
      position="relative"
      borderRadius="4px"
      overflow="hidden"
      {...boxProps}
      backgroundColor="blackAlpha.50"
    >
      {inView ? (
        <AspectRatio ratio={aspectRatio}>
          <Image
            src={
              location
                ? getCloudflareStaticMapUrlForMunicipality(municipality)
                : placeholderImage
            }
            alt={name || 'Municipality'}
            objectFit="cover"
            objectPosition="top"
            width="100%"
            height="100%"
            opacity={0.8}
            fallback={<ListSkeleton isLoaded={false} />}
            {...imageProps}
          />
        </AspectRatio>
      ) : (
        <Box minH="300px" backgroundColor="blackAlpha.50" />
      )}
    </Box>
  );
};
